/**
 * Прилипающий блок с плавным скрытием внутренних элементов
 *
 * @example
 * new StickyBlock({
 *     container: '.js-sticky',
 *     sensor: '.js-sticky-sensor',
 *     hideable: '.js-sticky-hide',
 *     minWidth: 768,
 * });
 */
class StickyBlock {
    /**
     * @param options - настройки
     * @param options.container - селектор прилипающего блока
     * @param options.sensor - селектор элемента-сенсора
     * @param options.hideable - селектор скрываемых элементов
     * @param options.stuckClass - класс прилипшего состояния
     * @param options.hideClass - класс скрытого состояния
     * @param options.deadzone - пиксели мёртвой зоны
     * @param options.cooldown - мс между переключениями
     * @param options.ignoreAfterToggle - мс игнорирования скролла после переключения
     * @param options.topOffset - отступ сверху в px
     * @param options.zIndex - z-index в прилипшем состоянии
     * @param options.minWidth - минимальная ширина viewport для работы (0 = всегда)
     * @param options.onStuck - колбэк при прилипании
     * @param options.onUnstuck - колбэк при отлипании
     */
    constructor(options) {
        const defaults = {
            container: '.js-sticky',
            sensor: '.js-sticky-sensor',
            hideable: null,
            stuckClass: 'stuck',
            hideClass: 'hidden',
            deadzone: 6,
            cooldown: 180,
            ignoreAfterToggle: 90,
            topOffset: 0,
            zIndex: 100,
            minWidth: 0,
            onStuck: null,
            onUnstuck: null,
        };

        this._options = {...defaults, ...options};
        this._$container = $(this._options.container);
        this._$sensor = $(this._options.sensor);

        this._$hideable = this._options.hideable
            ? this._$container.find(this._options.hideable)
            : $();

        if (!this._$container.length || !this._$sensor.length)
            return;

        this._raf = 0;
        this._stuck = false;
        this._lastScrollY = window.scrollY;
        this._lastToggleAt = -1e9;
        this._ignoreUntil = 0;
        this._triggerY = 0;
        this._$placeholder = null;

        this._boundOnScroll = () => this._onScroll();
        this._boundOnResize = () => this._onResize();
        this._boundUpdate = () => this._update();

        this._init();
    }

    // Геттеры

    get stuck() {
        return this._stuck;
    }

    /**
     * Проверяет, разрешена ли работа на текущем разрешении
     *
     * @returns true если ширина viewport >= minWidth
     */
    _isAllowedWidth() {
        return window.innerWidth >= this._options.minWidth;
    }

    /**
     * Инициализация обработчиков
     */
    _init() {
        window.addEventListener('scroll', this._boundOnScroll, {passive: true,});
        window.addEventListener('resize', this._boundOnResize);

        if (!this._isAllowedWidth())
            return;

        this._recalcTrigger();

        if (window.scrollY >= this._triggerY + this._options.deadzone)
            this._setStuck(true);
    }

    /**
     * Возвращает позицию сенсора относительно viewport
     *
     * @returns позиция top в пикселях
     */
    _getSensorTop() {
        return Math.round(this._$sensor[0].getBoundingClientRect().top);
    }

    /**
     * Пересчитывает точку триггера в координатах документа
     */
    _recalcTrigger() {
        this._triggerY = Math.round(
            this._getSensorTop() + window.scrollY - this._options.topOffset,
        );
    }

    /**
     * Применяет fixed-позиционирование и создаёт плейсхолдер
     */
    _applyFixed() {
        if (this._$placeholder)
            return;

        this._$placeholder = $('<div aria-hidden="true"></div>');
        this._$container.after(this._$placeholder);
        this._syncPlaceholder();

        const rect = this._$container[0].getBoundingClientRect();

        this._$container.css({
            position: 'fixed',
            top: this._options.topOffset,
            left: rect.left,
            width: rect.width,
            zIndex: this._options.zIndex,
        });
    }

    /**
     * Убирает fixed-позиционирование и удаляет плейсхолдер
     */
    _removeFixed() {
        if (!this._$placeholder)
            return;

        this._$container.css({
            position: '',
            top: '',
            left: '',
            width: '',
            zIndex: '',
        });

        this._$placeholder.remove();
        this._$placeholder = null;
    }

    /**
     * Синхронизирует высоту плейсхолдера с контейнером
     */
    _syncPlaceholder() {
        if (!this._$placeholder)
            return;

        const h = Math.round(this._$container[0].getBoundingClientRect().height);

        this._$placeholder.height(h);
    }

    /**
     * Переключает состояние прилипания с защитой от дребезга
     *
     * @param value - новое состояние
     */
    _setStuck(value) {
        if (this._stuck === value)
            return;

        const now = performance.now();

        if (now - this._lastToggleAt < this._options.cooldown)
            return;

        this._lastToggleAt = now;
        this._ignoreUntil = now + this._options.ignoreAfterToggle;
        this._stuck = value;

        if (value) {
            this._recalcTrigger();
            this._applyFixed();
        }

        this._$container.toggleClass(this._options.stuckClass, value);
        this._$hideable.toggleClass(this._options.hideClass, value);
        this._syncPlaceholder();

        if (value && this._options.onStuck)
            this._options.onStuck();

        if (!value && this._options.onUnstuck)
            this._options.onUnstuck();

        if (!value) {
            this._removeFixed();
            requestAnimationFrame(() => this._recalcTrigger());
        }
    }

    /**
     * Вычисляет положение и решает, прилипать или отлипать
     */
    _update() {
        this._raf = 0;

        if (!this._isAllowedWidth())
            return;

        const now = performance.now();

        if (now < this._ignoreUntil)
            return;

        const scrollY = window.scrollY;

        if (!this._stuck)
            this._recalcTrigger();

        const
            delta = scrollY - this._lastScrollY,
            scrollingDown = delta > 1,
            scrollingUp = delta < -1;

        this._lastScrollY = scrollY;

        if (
            !this._stuck &&
            scrollingDown &&
            scrollY >= this._triggerY + this._options.deadzone
        )
            this._setStuck(true);

        if (
            this._stuck &&
            scrollingUp &&
            scrollY <= this._triggerY - this._options.deadzone
        )
            this._setStuck(false);

        if (this._stuck)
            this._syncPlaceholder();
    }

    /**
     * Обработчик скролла с throttle через rAF
     */
    _onScroll() {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(this._boundUpdate);
    }

    /**
     * Обработчик ресайза
     */
    _onResize() {
        // Сбрасываем, если экран стал узким
        if (!this._isAllowedWidth()) {
            if (this._stuck)
                this._setStuck(false);

            return;
        }

        this._onScroll();

        if (!this._stuck)
            this._recalcTrigger();

        if (this._stuck) {
            const rect = this._$container[0].getBoundingClientRect();

            this._$container.css({
                left: rect.left,
                width: rect.width,
            });

            this._syncPlaceholder();
        }
    }

    /**
     * Уничтожает экземпляр и снимает обработчики
     */
    destroy() {
        window.removeEventListener('scroll', this._boundOnScroll);
        window.removeEventListener('resize', this._boundOnResize);
        cancelAnimationFrame(this._raf);
        this._removeFixed();
    }
}